import { Alert, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import KlaviyoBackfillCard from '../../Klaviyo/KlaviyoBackfillCard';
import KlaviyoConnectButton from '../../Klaviyo/KlaviyoConnectButton';
import KlaviyoDisconnectButton from '../../Klaviyo/KlaviyoDisconnectButton';
import KlaviyoSendMetricsButton from '../../Klaviyo/KlaviyoSendMetricsButton';
import KlaviyoSettings from '../../Klaviyo/KlaviyoSettings';

// The raw ?error value comes from the URL, so it must never be rendered verbatim - a crafted
// link could otherwise phish a super-admin with arbitrary text in an official-looking red
// banner. Map the backend's known error codes to fixed messages and fall back to a generic
// one for anything unrecognized.
const KLAVIYO_ERROR_MESSAGES = {
  access_denied: 'Klaviyo authorization was declined. Please try connecting again.',
  invalid_state: 'The Klaviyo connection request expired or was invalid. Please try connecting again.',
  missing_code: 'Klaviyo did not return an authorization code. Please try connecting again.',
  token_exchange_failed: 'Could not complete the Klaviyo connection. Please try again or contact support.',
  invalid_scope:
    'The Releva app in Klaviyo is not allowed to request the required permissions. Please contact Releva support.',
  missing_permissions:
    'The Klaviyo connection is missing required permissions - it needs read access to Events, Metrics and Profiles.',
  invalid_credentials: 'Klaviyo rejected the credentials - they are invalid or were revoked. Please reconnect.',
  rate_limited: 'Klaviyo is rate limiting requests right now - wait a minute and try again.',
  connection_failed: 'Could not reach the Klaviyo API. Please try again in a few minutes.',
};
const KLAVIYO_ERROR_FALLBACK = 'Could not connect Klaviyo. Please try again or contact support.';

const SettingsTabKlaviyo = ({ klaviyoAccount }) => {
  // The backend OAuth callback redirects to the emails tab with ?success=connected or
  // ?error=<code> - surface both here since this section owns the connect flow.
  const location = useLocation();
  const navigate = useNavigate();

  // Read the callback result once on mount, then strip ?success/?error from the URL below so
  // the banners don't persist across tab switches / refreshes.
  const [callbackResult] = useState(() => {
    const search = queryString.parse(location.search);
    return { success: search.success, error: search.error };
  });

  useEffect(() => {
    const search = queryString.parse(location.search);
    if (search.success === undefined && search.error === undefined) {
      return;
    }
    delete search.success;
    delete search.error;
    const nextSearch = queryString.stringify(search);
    navigate({ search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
  }, [location.search, navigate]);

  const errorMessage = callbackResult.error
    ? KLAVIYO_ERROR_MESSAGES[callbackResult.error] || KLAVIYO_ERROR_FALLBACK
    : undefined;

  return (
    <>
      {errorMessage && (
        <Alert color="red" icon={<IconAlertTriangle />} mb="md">
          {errorMessage}
        </Alert>
      )}
      {callbackResult.success === 'connected' && klaviyoAccount && (
        <Text size="sm" c="green" mb="md">
          Klaviyo connected successfully.
        </Text>
      )}

      {klaviyoAccount && !klaviyoAccount.credentialsInvalid ? (
        <>
          <KlaviyoSettings klaviyoAccount={klaviyoAccount} />
          <KlaviyoSendMetricsButton transactionalApproved={klaviyoAccount.transactionalApproved} />
          <KlaviyoBackfillCard klaviyoAccount={klaviyoAccount} />
          <KlaviyoDisconnectButton />
        </>
      ) : (
        <>
          {klaviyoAccount?.credentialsInvalid && (
            <Alert color="yellow" icon={<IconAlertTriangle />} mb="md">
              The Klaviyo connection is no longer valid - the app was uninstalled, the token expired, the API key was
              revoked or its permissions were reduced. Reconnect to resume the engagement sync.
            </Alert>
          )}
          <KlaviyoConnectButton />
          {klaviyoAccount && <KlaviyoDisconnectButton />}
        </>
      )}
    </>
  );
};

export default SettingsTabKlaviyo;
